import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { api } from '../api';
import { Upload, CheckCircle, Link, Plus, Trash2, Globe, Database, Activity, RefreshCw, Layers3, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CATEGORY_OPTIONS = ['General', 'Policy', 'Contract', 'Market', 'Training'];

const AdminPage = () => {
  const [files, setFiles] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [vectorStatus, setVectorStatus] = useState(null);
  const [opsMetrics, setOpsMetrics] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [ingestingUrl, setIngestingUrl] = useState(false);
  const [loadingVectorStatus, setLoadingVectorStatus] = useState(false);
  
  // Ingestion Metadata State
  const [ingestState, setIngestState] = useState('ALL');
  const [ingestCategories, setIngestCategories] = useState(['General']);

  // Integration Modal State
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const [newIntegration, setNewIntegration] = useState({ name: '', type: 'api', config: { url: '', api_key: '' } });
  const [editingSource, setEditingSource] = useState(null);
  const [savingSourceMetadata, setSavingSourceMetadata] = useState(false);
  const [deletingSourceId, setDeletingSourceId] = useState(null);

  const fetchData = async (refreshVectorStatus = false) => {
    setLoadingVectorStatus(true);
    try {
      const [filesRes, intRes, vectorRes, opsRes] = await Promise.all([
        api.get('/admin/files'),
        api.get('/integrations/'),
        api.get(`/admin/vector-store/status${refreshVectorStatus ? '?refresh=true' : ''}`),
        api.get('/admin/ops-metrics'),
      ]);
      setFiles(filesRes.data);
      setIntegrations(intRes.data);
      setVectorStatus(vectorRes.data);
      setOpsMetrics(opsRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load vector store status');
    } finally {
      setLoadingVectorStatus(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleCategorySelection = (category) => {
    setIngestCategories((current) => {
      const next = current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category];
      return next.length > 0 ? next : ['General'];
    });
  };

  const toggleEditingCategory = (category) => {
    setEditingSource((current) => {
      if (!current) return current;
      const next = current.categories.includes(category)
        ? current.categories.filter((item) => item !== category)
        : [...current.categories, category];
      return { ...current, categories: next.length > 0 ? next : ['General'] };
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('state', ingestState);
    formData.append('categories', JSON.stringify(ingestCategories));

    try {
      await api.post('/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${file.name} successfully`);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlIngest = async (e) => {
    e.preventDefault();
    if (!urlInput) return;
    
    setIngestingUrl(true);
    try {
        await api.post('/admin/ingest/url', { 
            url: urlInput,
            state: ingestState,
            categories: ingestCategories
        });
        toast.success(`Started ingestion for ${urlInput}`);
        setUrlInput('');
        setTimeout(fetchData, 2000);
    } catch (err) {
        console.error(err);
        toast.error('URL ingestion failed');
    } finally {
        setIngestingUrl(false);
    }
  };

  const handleCreateIntegration = async () => {
      try {
          await api.post('/integrations/', newIntegration);
          toast.success("Integration added");
          setIsIntegrationModalOpen(false);
          setNewIntegration({ name: '', type: 'api', config: { url: '', api_key: '' } });
          fetchData();
      } catch (err) {
          toast.error("Failed to create integration");
      }
  };

  const openEditSource = (source) => {
    setEditingSource({
      source_id: source.source_id,
      name: source.name,
      state: source.state || 'ALL',
      categories: source.categories?.length ? source.categories : ['General'],
      source_type: source.source_type,
    });
  };

  const handleSaveSourceMetadata = async () => {
    if (!editingSource) return;
    setSavingSourceMetadata(true);
    try {
      await api.put(`/admin/files/${editingSource.source_id}/metadata`, {
        state: editingSource.state,
        categories: editingSource.categories,
      });
      toast.success(`Updated tags for ${editingSource.name}`);
      setEditingSource(null);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update source tags');
    } finally {
      setSavingSourceMetadata(false);
    }
  };

  const handleDeleteSource = async (source) => {
    const confirmed = window.confirm(`Delete ${source.name} and remove it from RAG?`);
    if (!confirmed) return;

    setDeletingSourceId(source.source_id);
    try {
      await api.delete(`/admin/files/${source.source_id}`);
      toast.success(`${source.name} removed from the knowledge base`);
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete source');
    } finally {
      setDeletingSourceId(null);
    }
  };

  const handleTestIntegration = async (id) => {
      try {
          const res = await api.post(`/integrations/${id}/test`);
          if (res.data.status === 'success') {
              toast.success("Connection successful!");
          } else {
              toast.error(`Failed: ${res.data.error || res.data.code}`);
          }
      } catch (err) {
          toast.error("Test failed");
      }
  };

  const handleDeleteIntegration = async (id) => {
      try {
          await api.delete(`/integrations/${id}`);
          toast.success("Integration removed");
          fetchData();
      } catch (err) {
          toast.error("Delete failed");
      }
  };

  const modeBadgeClassName = vectorStatus?.native_available
    ? 'bg-emerald-500/10 text-emerald-300 border-emerald-400/20'
    : 'bg-amber-500/10 text-amber-200 border-amber-400/20';

  const modeLabel = vectorStatus?.native_available ? 'Atlas Vector Search' : 'Optimized Mongo Fallback';

  const renderCategoryPicker = (selected, onToggle, testIdPrefix) => (
    <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-category-picker`}>
      {CATEGORY_OPTIONS.map((category) => {
        const active = selected.includes(category);
        return (
          <Button
            key={category}
            type="button"
            variant="outline"
            onClick={() => onToggle(category)}
            data-testid={`${testIdPrefix}-category-${category.toLowerCase()}`}
            className={`border-white/10 text-xs ${active ? 'bg-cyan-500 text-black hover:bg-cyan-400' : 'bg-white/5 text-white hover:bg-white/10'}`}
          >
            {category}
          </Button>
        );
      })}
    </div>
  );

  const renderMetricPanel = (title, metric, testIdPrefix) => (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid={`${testIdPrefix}-panel`}>
      <div className="text-xs uppercase tracking-[0.2em] text-white/35">{title}</div>
      <div className="mt-3 grid gap-1 text-sm text-white/85">
        <div data-testid={`${testIdPrefix}-count`}>Count: {metric?.count ?? 0}</div>
        <div data-testid={`${testIdPrefix}-avg`}>Avg: {metric?.avg_ms ?? 0} ms</div>
        <div data-testid={`${testIdPrefix}-p95`}>P95: {metric?.p95_ms ?? 0} ms</div>
        <div data-testid={`${testIdPrefix}-max`}>Max: {metric?.max_ms ?? 0} ms</div>
      </div>
    </div>
  );

  return (
    <div className="h-full overflow-auto p-6 md:p-10 max-w-7xl mx-auto space-y-8 rounded-3xl lg-glass">
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold tracking-tight text-white">Knowledge Base</h1>
        <p className="text-white/50">Manage documents, web sources, and API integrations.</p>
      </div>

      <Card className="lg-glass-strong border-white/10 text-white" data-testid="vector-store-status-card">
        <CardHeader className="gap-4 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <CardTitle className="flex items-center gap-2">
              <Layers3 className="h-5 w-5 text-cyan-400" />
              Retrieval Engine
            </CardTitle>
            <CardDescription className="text-white/45 max-w-2xl" data-testid="vector-store-status-summary">
              Native MongoDB Vector Search is auto-enabled on supported Atlas deployments. Preview and unsupported environments use the optimized normalized-numpy fallback automatically.
            </CardDescription>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline" className={modeBadgeClassName} data-testid="vector-store-mode-badge">
              {modeLabel}
            </Badge>
            <Button
              type="button"
              variant="outline"
              onClick={() => fetchData(true)}
              disabled={loadingVectorStatus}
              className="border-white/10 bg-white/5 text-white hover:bg-white/10"
              data-testid="refresh-vector-store-status-button"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loadingVectorStatus ? 'animate-spin' : ''}`} />
              Refresh status
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="vector-store-provider-panel">
              <div className="text-xs uppercase tracking-[0.2em] text-white/35">Provider</div>
              <div className="mt-2 text-sm font-medium text-white/90" data-testid="vector-store-provider-value">
                {vectorStatus?.provider || 'Loading...'}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="vector-store-document-count-panel">
              <div className="text-xs uppercase tracking-[0.2em] text-white/35">Indexed Chunks</div>
              <div className="mt-2 text-2xl font-semibold text-white" data-testid="vector-store-document-count-value">
                {vectorStatus?.document_count ?? '—'}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="vector-store-index-panel">
              <div className="text-xs uppercase tracking-[0.2em] text-white/35">Index Name</div>
              <div className="mt-2 text-sm font-medium text-cyan-100 break-all" data-testid="vector-store-index-value">
                {vectorStatus?.index_name || 'Checking...'}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid="vector-store-availability-panel">
              <div className="text-xs uppercase tracking-[0.2em] text-white/35">Native Readiness</div>
              <div className="mt-2 text-sm font-medium text-white/90" data-testid="vector-store-availability-value">
                {vectorStatus?.native_available ? 'Ready for native search' : 'Fallback active'}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="vector-store-technical-details">
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/55">
              <span data-testid="vector-store-dimensions-value">Embedding size: {vectorStatus?.embedding_dimensions ?? '—'}</span>
              <span data-testid="vector-store-model-value">Model: {vectorStatus?.embedding_model || '—'}</span>
              <span data-testid="vector-store-fallback-value">Fallback: {vectorStatus?.fallback_strategy || '—'}</span>
              <span data-testid="vector-store-last-check-value">Last check: {vectorStatus?.last_checked_at ? new Date(vectorStatus.last_checked_at).toLocaleString() : 'Pending'}</span>
            </div>

            {vectorStatus?.last_error && (
              <div className="mt-3 rounded-xl border border-amber-400/15 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-amber-100/85" data-testid="vector-store-last-error">
                {vectorStatus.last_error}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="lg-glass-strong border-white/10 text-white" data-testid="ops-metrics-card">
        <CardHeader className="gap-3">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-cyan-400" />
            Operational Metrics
          </CardTitle>
          <CardDescription className="text-white/45" data-testid="ops-metrics-summary">
            Live runtime metrics for async thread offloading, ingestion duration, vector queries, and current rate-limit policy.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {renderMetricPanel('Thread Offload Timing', opsMetrics?.thread_offload, 'thread-offload')}
            {renderMetricPanel('Vector Query Timing', opsMetrics?.vector_query, 'vector-query')}
            {renderMetricPanel('Ingestion Duration', opsMetrics?.ingestion_duration, 'ingestion-duration')}
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="rate-limit-config-panel">
            <div className="text-xs uppercase tracking-[0.2em] text-white/35">Rate Limit Policy</div>
            <div className="mt-3 grid gap-3 md:grid-cols-3 text-sm text-white/80">
              <div data-testid="rate-limit-auth-config">
                <div className="font-medium text-white/95">Auth Login</div>
                <div>{opsMetrics?.rate_limit_config?.auth_login?.limit ?? 0} attempts / {Math.round((opsMetrics?.rate_limit_config?.auth_login?.window_seconds ?? 0) / 60)} min</div>
              </div>
              <div data-testid="rate-limit-rag-config">
                <div className="font-medium text-white/95">RAG Query</div>
                <div>{opsMetrics?.rate_limit_config?.rag_query?.limit ?? 0} requests / {Math.round((opsMetrics?.rate_limit_config?.rag_query?.window_seconds ?? 0) / 60)} min</div>
              </div>
              <div data-testid="rate-limit-optimize-config">
                <div className="font-medium text-white/95">Prompt Optimize</div>
                <div>{opsMetrics?.rate_limit_config?.rag_optimize?.limit ?? 0} requests / {Math.round((opsMetrics?.rate_limit_config?.rag_optimize?.window_seconds ?? 0) / 60)} min</div>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-cyan-500/10 bg-cyan-500/5 px-3 py-2 text-xs leading-relaxed text-cyan-100/85" data-testid="rate-limit-admin-policy">
              {opsMetrics?.rate_limit_config?.admin_policy}
            </div>

            <div className="mt-4 flex flex-wrap gap-3 text-xs text-white/55">
              <span data-testid="rate-limit-auth-allowed">Auth allowed: {opsMetrics?.rate_limits?.auth_login_allowed ?? 0}</span>
              <span data-testid="rate-limit-auth-blocked">Auth blocked: {opsMetrics?.rate_limits?.auth_login_blocked ?? 0}</span>
              <span data-testid="rate-limit-rag-allowed">RAG allowed: {opsMetrics?.rate_limits?.rag_query_allowed ?? 0}</span>
              <span data-testid="rate-limit-rag-blocked">RAG blocked: {opsMetrics?.rate_limits?.rag_query_blocked ?? 0}</span>
              <span data-testid="rate-limit-optimize-allowed">Optimize allowed: {opsMetrics?.rate_limits?.rag_optimize_allowed ?? 0}</span>
              <span data-testid="rate-limit-optimize-blocked">Optimize blocked: {opsMetrics?.rate_limits?.rag_optimize_blocked ?? 0}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="files" className="w-full">
        <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl mb-6">
          <TabsTrigger value="files" data-testid="admin-files-tab" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60">Files</TabsTrigger>
          <TabsTrigger value="web" data-testid="admin-web-tab" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60">Web Sources</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="admin-integrations-tab" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60">Integrations</TabsTrigger>
        </TabsList>

        <TabsContent value="files" className="space-y-6">
          {/* Metadata Controls */}
          <div className="grid gap-4 bg-white/5 p-4 rounded-xl border border-white/10 md:grid-cols-[220px_1fr]">
             <div className="flex-1 space-y-1">
                <Label className="text-xs text-white/50">State Context</Label>
                <Select value={ingestState} onValueChange={setIngestState}>
                    <SelectTrigger className="bg-black/20 border-white/10 text-white h-9" data-testid="upload-state-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="lg-glass-strong border-white/10 text-white">
                        <SelectItem value="ALL">All States</SelectItem>
                        <SelectItem value="VA">Virginia</SelectItem>
                        <SelectItem value="NC">North Carolina</SelectItem>
                    </SelectContent>
                </Select>
             </div>
             <div className="flex-1 space-y-2">
                <Label className="text-xs text-white/50">Category Tags</Label>
                {renderCategoryPicker(ingestCategories, toggleCategorySelection, 'upload')}
             </div>
          </div>

          {/* Upload Section */}
          <Card className="lg-glass-strong border-white/10 text-white" data-testid="admin-upload-dropzone">
            <CardHeader>
              <CardTitle>Upload Documents</CardTitle>
              <CardDescription className="text-white/40">Tagged as: {ingestState} / {ingestCategories.join(', ')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border border-dashed border-white/20 rounded-2xl p-10 flex flex-col items-center justify-center text-center hover:bg-white/5 transition-colors relative group">
                <div className="h-14 w-14 rounded-full bg-cyan-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Upload className="h-6 w-6 text-cyan-400" />
                </div>
                <h3 className="text-sm font-medium mb-1 text-white">Drag and drop or click to upload</h3>
                <p className="text-xs text-white/40 mb-6">Max file size 10MB</p>
                
                <div className="relative z-10">
                  <input 
                    type="file" 
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={handleFileUpload}
                    accept=".pdf,.txt,.docx"
                    data-testid="admin-upload-file-input"
                    disabled={uploading}
                  />
                  <Button disabled={uploading} className="bg-cyan-500 text-black hover:bg-cyan-400 font-semibold" data-testid="admin-upload-button">
                    {uploading ? 'Uploading...' : 'Select File'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Files List */}
          <Card className="lg-glass border-white/10 text-white bg-transparent">
            <CardHeader>
              <CardTitle>Ingested Files</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table data-testid="admin-files-table">
                <TableHeader className="bg-white/5 border-b border-white/10">
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/60">Filename</TableHead>
                    <TableHead className="text-white/60">State</TableHead>
                    <TableHead className="text-white/60">Categories</TableHead>
                    <TableHead className="text-white/60">Type</TableHead>
                    <TableHead className="text-white/60">Size</TableHead>
                    <TableHead className="text-white/60">Status</TableHead>
                    <TableHead className="text-white/60 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.length === 0 ? (
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableCell colSpan={7} className="text-center h-24 text-white/40">
                        No files found. Upload some documents to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    files.map((file, idx) => (
                      <TableRow key={file.source_id || idx} data-testid="admin-files-row" className="border-white/5 hover:bg-white/5 transition-colors">
                        <TableCell className="font-medium font-mono text-xs text-cyan-100 max-w-[220px]">
                          <div className="truncate" title={file.name}>{file.name}</div>
                          {file.source_url && <div className="mt-1 truncate text-[10px] text-white/35" title={file.source_url}>{file.source_url}</div>}
                        </TableCell>
                        <TableCell className="text-xs text-white/60">{file.state}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1" data-testid={`file-categories-${file.source_id}`}>
                            {(file.categories || []).map((category) => (
                              <Badge key={`${file.source_id}-${category}`} variant="outline" className="border-cyan-500/20 bg-cyan-500/10 text-[10px] text-cyan-300">
                                {category}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="uppercase text-xs text-white/60">
                          <div>{file.type}</div>
                          <div className="mt-1 text-[10px] text-white/30">{file.source_type}</div>
                        </TableCell>
                        <TableCell className="text-xs text-white/40">{(file.size / 1024).toFixed(1)} KB</TableCell>
                        <TableCell>
                          {file.status === 'ready' ? (
                            <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20 gap-1 hover:bg-green-500/20">
                              <CheckCircle className="h-3 w-3" /> Ready
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-200 border-amber-500/20 gap-1 hover:bg-amber-500/20">
                              Processing
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="ghost" size="sm" onClick={() => openEditSource(file)} data-testid={`edit-source-${file.source_id}`} className="h-8 gap-1 text-cyan-300 hover:bg-cyan-500/10 hover:text-cyan-200">
                              <Pencil className="h-3 w-3" /> Edit
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteSource(file)} disabled={deletingSourceId === file.source_id} data-testid={`delete-source-${file.source_id}`} className="h-8 gap-1 text-red-300 hover:bg-red-500/10 hover:text-red-200">
                              <Trash2 className="h-3 w-3" /> Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="web" className="space-y-6">
            <div className="grid gap-4 bg-white/5 p-4 rounded-xl border border-white/10 md:grid-cols-[220px_1fr]">
                 <div className="flex-1 space-y-1">
                    <Label className="text-xs text-white/50">State Context</Label>
                    <Select value={ingestState} onValueChange={setIngestState}>
                        <SelectTrigger className="bg-black/20 border-white/10 text-white h-9" data-testid="web-state-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="lg-glass-strong border-white/10 text-white">
                            <SelectItem value="ALL">All States</SelectItem>
                            <SelectItem value="VA">Virginia</SelectItem>
                            <SelectItem value="NC">North Carolina</SelectItem>
                        </SelectContent>
                    </Select>
                 </div>
                 <div className="flex-1 space-y-2">
                    <Label className="text-xs text-white/50">Category Tags</Label>
                    {renderCategoryPicker(ingestCategories, toggleCategorySelection, 'web')}
                 </div>
            </div>

            <Card className="lg-glass-strong border-white/10 text-white">
                <CardHeader>
                    <CardTitle>Ingest Website</CardTitle>
                    <CardDescription className="text-white/40">Crawl a URL and all its internal pages to add content to the knowledge base.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <form onSubmit={handleUrlIngest} className="flex gap-4">
                        <div className="flex-1 relative">
                            <Link className="absolute left-3 top-2.5 h-4 w-4 text-white/40" />
                            <Input 
                                placeholder="https://reinmls.com" 
                                className="pl-9 bg-black/20 border-white/10 text-white placeholder:text-white/20 focus-visible:ring-cyan-500/50"
                                value={urlInput}
                                onChange={(e) => setUrlInput(e.target.value)}
                                data-testid="url-ingest-input"
                                required
                            />
                        </div>
                        <Button type="submit" disabled={ingestingUrl} data-testid="url-ingest-btn" className="bg-cyan-500 text-black hover:bg-cyan-400 font-semibold">
                            {ingestingUrl ? 'Crawling...' : 'Crawl & Ingest'}
                        </Button>
                    </form>
                    <p className="text-xs text-white/30">The crawler will follow internal navigation links and ingest up to 20 pages from the same domain.</p>
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="integrations" className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-white">External Sources</h3>
                <Button onClick={() => setIsIntegrationModalOpen(true)} className="bg-cyan-500 text-black hover:bg-cyan-400 gap-2">
                    <Plus className="h-4 w-4" /> Add Source
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {integrations.map((item) => (
                    <Card key={item.id} className="lg-glass border-white/10 text-white hover:bg-white/5 transition-colors">
                        <CardHeader className="pb-2">
                            <div className="flex justify-between items-start">
                                <div className="flex items-center gap-2">
                                    {item.type === 'mcp' ? <Database className="h-5 w-5 text-purple-400" /> : <Globe className="h-5 w-5 text-blue-400" />}
                                    <CardTitle className="text-base">{item.name}</CardTitle>
                                </div>
                                <Badge variant="secondary" className="uppercase text-[10px] tracking-wider">{item.type}</Badge>
                            </div>
                            <CardDescription className="text-xs font-mono truncate">{item.config.url}</CardDescription>
                        </CardHeader>
                        <CardContent className="pt-2 flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleTestIntegration(item.id)} className="h-8 text-xs gap-1 hover:text-green-400 hover:bg-green-900/20">
                                <Activity className="h-3 w-3" /> Test Connection
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteIntegration(item.id)} className="h-8 w-8 p-0 text-white/30 hover:text-red-400 hover:bg-red-900/20">
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!editingSource} onOpenChange={(open) => !open && setEditingSource(null)}>
        <DialogContent className="lg-glass-strong border-white/10 text-white" data-testid="edit-source-dialog">
          <DialogHeader>
            <DialogTitle>Edit Knowledge Source</DialogTitle>
            <DialogDescription className="text-white/40">
              Update State Context and Category tags for this source. Changes apply to uploaded files and crawled pages in RAG.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs text-white/50">Source</Label>
              <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/80" data-testid="edit-source-name">
                {editingSource?.name}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-white/50">State Context</Label>
              <Select value={editingSource?.state || 'ALL'} onValueChange={(value) => setEditingSource((current) => current ? { ...current, state: value } : current)}>
                <SelectTrigger className="bg-black/20 border-white/10 text-white" data-testid="edit-source-state-select"><SelectValue /></SelectTrigger>
                <SelectContent className="lg-glass-strong border-white/10 text-white">
                  <SelectItem value="ALL">All States</SelectItem>
                  <SelectItem value="VA">Virginia</SelectItem>
                  <SelectItem value="NC">North Carolina</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-white/50">Category Tags</Label>
              {renderCategoryPicker(editingSource?.categories || ['General'], toggleEditingCategory, 'edit-source')}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setEditingSource(null)} data-testid="edit-source-cancel-button">Cancel</Button>
            <Button type="button" onClick={handleSaveSourceMetadata} disabled={savingSourceMetadata} className="bg-cyan-500 text-black hover:bg-cyan-400" data-testid="edit-source-save-button">
              {savingSourceMetadata ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Integration Modal */}
      <Dialog open={isIntegrationModalOpen} onOpenChange={setIsIntegrationModalOpen}>
        <DialogContent className="lg-glass-strong border-white/10 text-white">
            <DialogHeader>
                <DialogTitle>Add Integration</DialogTitle>
                <DialogDescription>Connect an external API or MCP Server.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
                <div className="space-y-2">
                    <Label>Name</Label>
                    <Input 
                        value={newIntegration.name} 
                        onChange={(e) => setNewIntegration({...newIntegration, name: e.target.value})}
                        className="bg-black/20 border-white/10 text-white" 
                    />
                </div>
                <div className="space-y-2">
                    <Label>Type</Label>
                    <Select 
                        value={newIntegration.type} 
                        onValueChange={(val) => setNewIntegration({...newIntegration, type: val})}
                    >
                        <SelectTrigger className="bg-black/20 border-white/10 text-white">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="lg-glass-strong border-white/10 text-white">
                            <SelectItem value="api">REST API (JSON)</SelectItem>
                            <SelectItem value="mcp">MCP Server (SSE)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label>URL</Label>
                    <Input 
                        value={newIntegration.config.url} 
                        onChange={(e) => setNewIntegration({...newIntegration, config: {...newIntegration.config, url: e.target.value}})}
                        className="bg-black/20 border-white/10 text-white" 
                    />
                </div>
                <div className="space-y-2">
                    <Label>{newIntegration.type === 'mcp' ? 'Secret / Key (Optional)' : 'API Key (Optional)'}</Label>
                    <Input 
                        type="password"
                        value={newIntegration.config.api_key} 
                        onChange={(e) => setNewIntegration({...newIntegration, config: {...newIntegration.config, api_key: e.target.value}})}
                        className="bg-black/20 border-white/10 text-white" 
                    />
                </div>
            </div>
            <DialogFooter>
                <Button variant="ghost" onClick={() => setIsIntegrationModalOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateIntegration} className="bg-cyan-500 text-black hover:bg-cyan-400">Add Source</Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
